/**
 * The cruise view's direction markers: the velocity pair (prograde and
 * retrograde), the body the pilot has picked as the flight target with its
 * name beside it, where the flight assist is bringing the motion round to, and
 * where the delta-v map's armed plan wants the nose.
 *
 * One module because they share a frame. The target glyph gives way where
 * it would land on a velocity marker, which needs all the drawn screen
 * positions in the same pass, and all of them clamp against the same HUD-panel
 * rects - a second module would double the `getBoundingClientRect()` flush the
 * cache below throttles.
 */
import * as THREE from 'three';

const TEX = 128;
const PROGRADE_COLOR = '#46e6c8';
const RETROGRADE_COLOR = '#c07cff';
const TARGET_COLOR = '#ffc14d';
const ASSIST_AIM_COLOR = '#ff5a5a';
const BURN_COLOR = '#6cb6ff';
/** CSS pixels across, whatever the resolution or the field of view is. */
const GLYPH_PX = 34;
/** The arrowhead rides the rim of this square, so it reaches GLYPH_PX past the glyph. */
const ARROW_PX = 96;
/**
 * The clamp rect's inset from the frame, in CSS pixels. Half the arrow's own
 * square plus a few, so a clamped marker and its arrowhead stay whole on screen.
 * This is the OUTER bound only: a HUD panel nearer the centre than this pulls
 * the clamp in further, per edge and per position along it.
 */
const MARGIN_PX = 56;
/**
 * Each HUD panel's own rect is grown by this before the clamp is asked to stop
 * short of it: half the 48 px box a marker is read in, plus a couple for a
 * panel's own border and shadow, so the whole glyph clears the panel rather
 * than only its centre.
 */
const PANEL_CLEAR_PX = 26;
/**
 * How often the panel rects are re-read, in ms. `getBoundingClientRect()` on
 * every panel forces a layout flush, so it must not run per frame: measured on
 * the cruise HUD's own eleven panels at 1600x900, one read costs 0.3 ms median
 * and 5.0 ms at its worst with the HUD's per-frame text writes dirtying layout
 * ahead of it. At this interval that is four reads a second. A resize skips
 * the wait; see `dirty` below.
 */
const PANEL_RECT_INTERVAL_MS = 250;
/** Metres in front of the eye. A marker is a direction, so the distance is only a scale. */
const EYE_DISTANCE_M = 40;
/** Under this there is no direction to draw and both velocity markers go away. */
const MIN_SPEED_MPS = 1e-3;
/** The label's own text height in CSS px, whatever the resolution or the fov is. */
const LABEL_PX = 14;
/** Texture pixels the label's font is laid out at, downscaled to LABEL_PX on screen. */
const LABEL_FONT_PX = 28;
/** Canvas rows that font is drawn in: room for its ascender, descender and glow. */
const LABEL_TEX_H = 42;
/** Texture columns of clear canvas each side of the text. */
const LABEL_TEX_PAD = 6;
const LABEL_FONT = `${LABEL_FONT_PX}px "SF Mono", "Menlo", "Consolas", "Courier New", monospace`;
/** CSS px of clear air between the glyph's own square and the label's. */
const LABEL_GAP_PX = 6;
/**
 * How often the label canvas may be rebuilt, in ms. The range in it moves its
 * last digit most frames, and a canvas texture upload per frame is not what
 * that costs: four a second is under the eye's own reading rate and is the
 * same interval the panel rects above are re-read on.
 */
const LABEL_REBUILD_INTERVAL_MS = 250;
/**
 * Show the target glyph again under this fraction of the hide threshold. The
 * body's drawn radius walks across that threshold as the ship closes, and
 * without a gap the glyph flickers on the frames either side of it.
 */
const TARGET_SHOW_FRACTION = 0.7;
/**
 * CSS px between the target glyph and a velocity marker inside which the
 * target glyph gives way: the 48 px box `--targetmark` reads a marker's own
 * pixels in, so two glyphs nearer than this share pixels in each other's box.
 * The label stays - flying straight at the target is exactly when the
 * prograde marker already marks the spot and the name is what is missing.
 */
const TARGET_CLASH_PX = 48;
/**
 * `asin`'s own argument, capped. `rangeM` is the range to the body's SURFACE,
 * so the ratio passes 1 inside a body's own radius of it and `asin` would
 * return NaN; `tan` of the angle then runs away as well. A body that close
 * fills the frame and is past any threshold this is compared to, so the cap
 * only has to be finite and well over it.
 */
const RADIUS_RATIO_MAX = 0.999;
/**
 * Degrees off prograde under which the assist-aim marker is hidden rather
 * than drawn on top of it. GLYPH_PX (34) is the glyph's own width; at this
 * game's 55 degree vertical fov and a typical 900 px tall viewport that is
 * about 16 px per degree, so half the glyph's width is already close to a
 * degree - nearer than that and the two glyphs share pixels.
 */
const ASSIST_AIM_HIDE_DEG = 1;
/** The assist-aim marker's own label. What it names never changes, so the text does not either. */
const ASSIST_AIM_LABEL = 'ASSIST AIM';

function glyphCanvas(color: string, retro: boolean): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = TEX;
  canvas.height = TEX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('progradeMarkers: 2d context unavailable');
  const c = TEX / 2;
  const r = 22;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineCap = 'round';
  ctx.lineWidth = 6;
  ctx.shadowColor = color;
  ctx.shadowBlur = 10;

  ctx.beginPath();
  ctx.arc(c, c, r, 0, Math.PI * 2);
  ctx.stroke();

  // Three spokes: up/left/right on prograde, down/left/right on retrograde.
  const spoke = 20;
  ctx.beginPath();
  ctx.moveTo(c - r, c); ctx.lineTo(c - r - spoke, c);
  ctx.moveTo(c + r, c); ctx.lineTo(c + r + spoke, c);
  if (retro) { ctx.moveTo(c, c + r); ctx.lineTo(c, c + r + spoke); }
  else { ctx.moveTo(c, c - r); ctx.lineTo(c, c - r - spoke); }
  ctx.stroke();

  if (retro) {
    const d = r * 0.62;
    ctx.beginPath();
    ctx.moveTo(c - d, c - d); ctx.lineTo(c + d, c + d);
    ctx.moveTo(c + d, c - d); ctx.lineTo(c - d, c + d);
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.arc(c, c, 5, 0, Math.PI * 2);
    ctx.fill();
  }
  return canvas;
}

/**
 * Four corner brackets, read as a bracket rather than the velocity pair's
 * circle and spokes at a glance and at a distance.
 */
function targetCanvas(color: string): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = TEX;
  canvas.height = TEX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('progradeMarkers: 2d context unavailable');
  const c = TEX / 2;
  const r = 26;
  const arm = 16;
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 7;
  ctx.shadowColor = color;
  ctx.shadowBlur = 10;
  ctx.beginPath();
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      const x = c + sx * r;
      const y = c + sy * r;
      ctx.moveTo(x - sx * arm, y);
      ctx.lineTo(x, y);
      ctx.lineTo(x, y - sy * arm);
    }
  }
  ctx.stroke();
  return canvas;
}

/** A diamond with a centre dot: read as a third glyph against the velocity pair's circles and the target's brackets. */
function assistAimCanvas(color: string): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = TEX;
  canvas.height = TEX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('progradeMarkers: 2d context unavailable');
  const c = TEX / 2;
  const r = 22;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 6;
  ctx.shadowColor = color;
  ctx.shadowBlur = 10;
  ctx.beginPath();
  ctx.moveTo(c, c - r);
  ctx.lineTo(c + r, c);
  ctx.lineTo(c, c + r);
  ctx.lineTo(c - r, c);
  ctx.closePath();
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(c, c, 4, 0, Math.PI * 2);
  ctx.fill();
  return canvas;
}

/** The picked body's name and range, as wide as the text needs. */
function labelCanvas(text: string, color: string): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('progradeMarkers: 2d context unavailable');
  ctx.font = LABEL_FONT;
  const width = Math.max(1, Math.ceil(ctx.measureText(text).width));
  canvas.width = width + LABEL_TEX_PAD * 2;
  canvas.height = LABEL_TEX_H;
  // Resizing the canvas resets the context, so the font is set again here.
  ctx.font = LABEL_FONT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = color;
  ctx.shadowColor = color;
  ctx.shadowBlur = 6;
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  return canvas;
}

/** A triangle with a centre dot: the fourth glyph, read against the velocity
 *  pair's circles, the target's brackets and the assist aim's diamond. */
function burnCanvas(color: string): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = TEX;
  canvas.height = TEX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('progradeMarkers: 2d context unavailable');
  const c = TEX / 2;
  const r = 24;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 6;
  ctx.shadowColor = color;
  ctx.shadowBlur = 10;
  ctx.beginPath();
  ctx.moveTo(c, c - r);
  ctx.lineTo(c + r * 0.87, c + r * 0.5);
  ctx.lineTo(c - r * 0.87, c + r * 0.5);
  ctx.closePath();
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(c, c + r * 0.12, 4, 0, Math.PI * 2);
  ctx.fill();
  return canvas;
}

/** An arrowhead at the right rim, pointing out. Rotated about the centre to aim. */
function arrowCanvas(color: string): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = TEX;
  canvas.height = TEX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('progradeMarkers: 2d context unavailable');
  const c = TEX / 2;
  ctx.fillStyle = color;
  ctx.shadowColor = color;
  ctx.shadowBlur = 10;
  ctx.beginPath();
  ctx.moveTo(TEX - 8, c);
  ctx.lineTo(TEX - 34, c - 17);
  ctx.lineTo(TEX - 34, c + 17);
  ctx.closePath();
  ctx.fill();
  return canvas;
}

function sprite(canvas: HTMLCanvasElement): THREE.Sprite {
  const tex = new THREE.CanvasTexture(canvas);
  tex.anisotropy = 4;
  const mat = new THREE.SpriteMaterial({
    map: tex,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const s = new THREE.Sprite(mat);
  s.visible = false;
  s.renderOrder = 1000;
  return s;
}

export interface ProgradeMarkerRead {
  visible: boolean;
  /** False when the direction left the frame and the marker rides the clamp rect. */
  onScreen: boolean;
  /** The DRAWN sprite's own place, projected by the same camera that drew it. */
  ndc: { x: number; y: number };
  px: { x: number; y: number };
  arrow: { visible: boolean; angleRad: number };
}

/** A label sprite's own drawn place, whether or not the glyph beside it is. */
export interface LabelMarkRead {
  visible: boolean;
  px: { x: number; y: number };
  widthPx: number;
  heightPx: number;
}

/** The picked body's marker, alongside the two velocity markers' own reads. */
export interface TargetMarkerRead {
  /** Has the pilot picked a body at all? Everything below is idle when not. */
  picked: boolean;
  /** `?targetmarkoff=1`: the handle stays, this marker draws nothing. */
  off: boolean;
  /** `?targetmarkalways=1`: both suppression rules are skipped. */
  always: boolean;
  /** The string the label's own texture carries, which the rebuild throttles. */
  label: string;
  /** The string the caller asked for this frame, before that throttle. */
  labelWanted: string;
  /** Label canvases built since boot, so the throttle above reads as a number. */
  labelBuilds: number;
  /** How the label keeps its side of the glyph, so a control that did not open reads as one. */
  labelHold: LabelHold;
  visible: boolean;
  onScreen: boolean;
  ndc: { x: number; y: number };
  px: { x: number; y: number };
  arrow: { visible: boolean; angleRad: number };
  /** The body's own drawn radius in CSS px, which the size rule is about. */
  bodyRadiusPx: number;
  /** Metres to the body's surface, as the caller handed it over. */
  rangeM: number;
  /** Which rule took the glyph away, or null if none did. */
  suppressed: 'size' | 'clash' | null;
  labelMark: LabelMarkRead;
}

/** One HUD panel's own live rect, in CSS pixels, as the clamp read it. */
export interface ProgradePanelRect {
  name: string;
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface ProgradeMarkersRead {
  speedMps: number;
  suppressed: boolean;
  eyeDistanceM: number;
  glyphPx: number;
  /** The camera's own vertical field of view in degrees, which every px scale rides. */
  fovDeg: number;
  /** The clamp rect's OUTER inset, before any panel pulls it in. */
  marginPx: number;
  /** What each panel's rect is grown by before the clamp stops short of it. */
  panelClearPx: number;
  /** False under `?hudinsetnone=1`: the clamp rides `marginPx` on every edge. */
  hudInsets: boolean;
  /** The panel rects the clamp last used, so a stale cache reads as one. */
  panels: ProgradePanelRect[];
  viewport: { width: number; height: number };
  eyeWorld: { x: number; y: number; z: number };
  /** projection * matrixWorldInverse, column-major, as three.js holds it. */
  viewProjection: number[];
  /** Rotates a flight-frame vector into three.js world axes. */
  flightToWorld: { x: number; y: number; z: number; w: number };
  prograde: ProgradeMarkerRead;
  retrograde: ProgradeMarkerRead;
  targetMark: TargetMarkerRead;
  /** Where the assist is bringing the motion round to. Idle (hidden) with no live aim. */
  assistAim: ProgradeMarkerRead & { labelMark: LabelMarkRead };
  /** Where the delta-v map's plan wants the nose. Idle with no plan armed. */
  burnMark: BurnMarkerRead;
}

/** The burn marker's own read: where it was drawn, and which burn it is for. */
export interface BurnMarkerRead extends ProgradeMarkerRead {
  /** Null while nothing is drawn. */
  kind: 'departure' | 'fix' | null;
  /** The string its label texture carries. */
  label: string;
  /** The unit direction the glyph was placed along, in the frame the caller
   *  handed it in. What a pilot points the nose at, read off the marker. */
  dir: { x: number; y: number; z: number } | null;
  labelMark: LabelMarkRead;
}

/** Where the delta-v map wants the nose, and what to write beside the glyph. */
export interface MarkerBurn {
  /** Unit direction, in the flight state's own frame. */
  dir: THREE.Vector3;
  /** The plan's own departure, or the fix that puts the ship back on it. */
  kind: 'departure' | 'fix';
  /** Already formatted by the caller. This module draws it and formats nothing. */
  label: string;
}

/** Where the picked body is, and what to write beside its marker. */
export interface MarkerTarget {
  /** Unit direction to the body, in the flight state's own frame. */
  dir: THREE.Vector3;
  /** Metres to the body's surface. The size rule's own range. */
  rangeM: number;
  /** The body's own radius in metres, off its registry row. */
  radiusM: number;
  /** Already formatted by the caller. This module draws it and formats nothing. */
  label: string;
}

export interface ProgradeMarkersConfig {
  camera: THREE.PerspectiveCamera;
  /** Rotates a flight-frame vector into the frame this handle's group is mounted in. */
  toMount: () => THREE.Quaternion;
  /** `?progradeoff=1`: keep the handle, draw neither velocity marker. */
  suppressed?: boolean;
  /** `?hudinsetnone=1`: ignore the panels and clamp on the bare margin rect. */
  ignorePanels?: boolean;
  /** `?targetmarkoff=1`: keep the handle, draw no target marker. */
  targetSuppressed?: boolean;
  /** `?targetmarkalways=1`: skip both of the target marker's suppression rules. */
  targetAlways?: boolean;
  /** How a label keeps its side: `held` (the game's), or the DEV controls `frame`, decided
   *  afresh every frame, and `frozen`, decided once and never changed. */
  labelHold?: LabelHold;
}

export type LabelHold = 'held' | 'frame' | 'frozen';

export interface ProgradeMarkers {
  group: THREE.Group;
  /**
   * `velocityFlight` is the velocity in the flight state's own frame, metres
   * per second; `target` is the picked body, or null when none is picked;
   * `assistAim` is the assist's own commanded direction in that same frame,
   * or null while it has none to hold or is off; `burn` is where the delta-v
   * map's armed plan wants the nose, or null while it wants it nowhere.
   */
  update: (velocityFlight: THREE.Vector3, target: MarkerTarget | null,
    assistAim: THREE.Vector3 | null, burn?: MarkerBurn | null) => void;
  read: () => ProgradeMarkersRead;
  dispose: () => void;
}

interface Marker {
  glyph: THREE.Sprite;
  arrow: THREE.Sprite;
  onScreen: boolean;
  angleRad: number;
  /** The DRAWN NDC, recorded as the glyph is placed - what the clash rule reads. */
  ndcX: number;
  ndcY: number;
  /** The side its label rides, -1 left or 1 right; 0 while hidden, so a return decides afresh. */
  labelSide: -1 | 0 | 1;
}

/**
 * The body's own drawn radius in CSS px at this range and field of view: its
 * angular radius `asin(radius/range)` through the camera's own `tan(fov/2)`
 * and the viewport height.
 */
function bodyRadiusPx(radiusM: number, rangeM: number, fovDeg: number,
  heightPx: number): number {
  if (!(radiusM > 0)) return 0;
  const ratio = Math.min(RADIUS_RATIO_MAX, radiusM / Math.max(1, rangeM));
  const theta = Math.asin(ratio);
  return (Math.tan(theta) / Math.tan((fovDeg * Math.PI) / 360)) * (heightPx / 2);
}

/**
 * Every visible HUD panel's own rect, by the `[data-widget]` selector
 * `tools/preview/layout-check.mjs` reads the layout with. No panel is named
 * here: a panel that moves, collapses into the top-right stack or is added
 * later moves this reading with it.
 */
function readPanelRects(): ProgradePanelRect[] {
  const out: ProgradePanelRect[] = [];
  for (const el of document.querySelectorAll<HTMLElement>('[data-widget]')) {
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') continue;
    if (Number(style.opacity) === 0) continue;
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) continue;
    out.push({ name: el.getAttribute('data-widget') ?? '', left: r.left, top: r.top,
      right: r.right, bottom: r.bottom });
  }
  return out;
}

/**
 * How far along the ray `(sx, sy)` a clamped marker may ride: the margin rect,
 * or the first panel the ray enters, whichever comes first. The answer is a
 * scale on the ray itself, so the clamped point stays an exact positive
 * multiple of the direction - the marker's angle from the frame centre is the
 * direction's own angle whether or not a panel shortened it.
 *
 * A uniform inset per edge cannot do this job on this HUD: the left and right
 * rails between them cover every horizontal line of the frame, so no single
 * top inset clears the panels at any height.
 */
function clearScale(sx: number, sy: number, rx: number, ry: number,
  width: number, height: number, panels: readonly ProgradePanelRect[]): number {
  const over = Math.max(Math.abs(sx) / rx, Math.abs(sy) / ry);
  let u = over > 1e-9 ? 1 / over : 0;
  for (const p of panels) {
    // The grown rect in NDC. Top and bottom swap: NDC y runs up, CSS y runs down.
    const l = (2 * (p.left - PANEL_CLEAR_PX)) / width - 1;
    const r = (2 * (p.right + PANEL_CLEAR_PX)) / width - 1;
    const t = 1 - (2 * (p.top - PANEL_CLEAR_PX)) / height;
    const b = 1 - (2 * (p.bottom + PANEL_CLEAR_PX)) / height;
    let enter = -Infinity;
    let exit = Infinity;
    if (Math.abs(sx) < 1e-12) {
      if (l > 0 || r < 0) continue;
    } else {
      const a = l / sx;
      const c = r / sx;
      enter = Math.min(a, c);
      exit = Math.max(a, c);
    }
    if (Math.abs(sy) < 1e-12) {
      if (b > 0 || t < 0) continue;
    } else {
      const a = b / sy;
      const c = t / sy;
      enter = Math.max(enter, Math.min(a, c));
      exit = Math.min(exit, Math.max(a, c));
    }
    // enter <= 0 means the frame centre itself is inside this panel, which
    // leaves no clear point on the ray at all: keep the margin rect instead.
    if (enter >= exit || enter <= 0) continue;
    if (enter < u) u = enter;
  }
  return u;
}

export function createProgradeMarkers(config: ProgradeMarkersConfig): ProgradeMarkers {
  const { camera } = config;
  const suppressed = config.suppressed === true;
  const hudInsets = config.ignorePanels !== true;
  const targetOff = config.targetSuppressed === true;
  const targetAlways = config.targetAlways === true;
  const labelHold = config.labelHold ?? 'held';
  const group = new THREE.Group();
  group.name = 'prograde-markers';

  let panels: ProgradePanelRect[] = [];
  let panelsReadAt = -Infinity;
  let panelsDirty = true;
  const invalidatePanels = (): void => { panelsDirty = true; };
  window.addEventListener('resize', invalidatePanels);

  /** Re-reads the panel rects when the interval has run out, or a resize asked. */
  function refreshPanels(now: number): void {
    if (!hudInsets) return;
    if (!panelsDirty && now - panelsReadAt < PANEL_RECT_INTERVAL_MS) return;
    panels = readPanelRects();
    panelsReadAt = now;
    panelsDirty = false;
  }

  function makeMarker(glyphCanvasOf: () => HTMLCanvasElement, color: string): Marker {
    const glyph = sprite(glyphCanvasOf());
    const arrow = sprite(arrowCanvas(color));
    group.add(glyph, arrow);
    return { glyph, arrow, onScreen: false, angleRad: 0, ndcX: 0, ndcY: 0, labelSide: 0 };
  }
  const prograde = makeMarker(() => glyphCanvas(PROGRADE_COLOR, false), PROGRADE_COLOR);
  const retrograde = makeMarker(() => glyphCanvas(RETROGRADE_COLOR, true), RETROGRADE_COLOR);
  const target = makeMarker(() => targetCanvas(TARGET_COLOR), TARGET_COLOR);
  const aimMarker = makeMarker(() => assistAimCanvas(ASSIST_AIM_COLOR), ASSIST_AIM_COLOR);
  const label = sprite(labelCanvas(' ', TARGET_COLOR));
  group.add(label);
  const aimLabelCanvas = labelCanvas(ASSIST_AIM_LABEL, ASSIST_AIM_COLOR);
  const aimLabel = sprite(aimLabelCanvas);
  group.add(aimLabel);
  const aimLabelHeightPx = LABEL_PX * (LABEL_TEX_H / LABEL_FONT_PX);
  const aimLabelWidthPx = aimLabelHeightPx * (aimLabelCanvas.width / aimLabelCanvas.height);
  const burnMarker = makeMarker(() => burnCanvas(BURN_COLOR), BURN_COLOR);
  const burnLabel = sprite(labelCanvas(' ', BURN_COLOR));
  group.add(burnLabel);

  /** Everything about the burn marker that is not the glyph's own placement. */
  const bm = {
    kind: null as 'departure' | 'fix' | null,
    dir: null as { x: number; y: number; z: number } | null,
    labelText: ' ',
    labelAspect: 1,
    labelWidthPx: 0,
    labelHeightPx: 0,
  };

  /** Everything about the target marker that is not the glyph's own placement. */
  const tm = {
    picked: false,
    labelText: ' ',
    labelWanted: '',
    labelBuilds: 0,
    labelBuiltAt: -Infinity,
    labelAspect: 1,
    labelWidthPx: 0,
    labelHeightPx: 0,
    radiusPx: 0,
    rangeM: 0,
    sizeHidden: false,
    suppressed: null as 'size' | 'clash' | null,
  };

  const _dir = new THREE.Vector3();
  const _opp = new THREE.Vector3();
  const _tgt = new THREE.Vector3();
  const _aim = new THREE.Vector3();
  const _burn = new THREE.Vector3();
  const _cam = new THREE.Vector3();
  const _ray = new THREE.Vector3();
  const _right = new THREE.Vector3();
  const _invCam = new THREE.Quaternion();
  const _vp = new THREE.Matrix4();
  const _eye = new THREE.Vector3();
  const _proj = new THREE.Vector3();
  const _quat = new THREE.Quaternion();
  let speedMps = 0;

  function hide(m: Marker): void {
    m.glyph.visible = false;
    m.arrow.visible = false;
    m.onScreen = false;
    m.labelSide = 0;
  }

  /**
   * The side of its glyph a label rides: toward the frame centre, so a clamped
   * name runs inward and clear of the arrowhead. A pinned body's glyph sits on
   * the centre line, where that sign is rounding noise, so a held side is kept
   * until the glyph is further past the centre than the label sits from it.
   */
  function labelSide(m: Marker, offsetPx: number, width: number): -1 | 1 {
    const facing = m.ndcX > 0 ? -1 : 1;
    if (m.labelSide !== 0 && m.labelSide !== facing) {
      if (labelHold === 'frozen') return m.labelSide;
      if (labelHold === 'held' && (Math.abs(m.ndcX) * width) / 2 <= offsetPx) return m.labelSide;
    }
    m.labelSide = facing;
    return facing;
  }

  function place(m: Marker, dirMount: THREE.Vector3, width: number, height: number): void {
    const t = Math.tan((camera.fov * Math.PI) / 360);
    const aspect = camera.aspect > 0 ? camera.aspect : width / Math.max(1, height);
    const scale = (GLYPH_PX * 2 * EYE_DISTANCE_M * t) / Math.max(1, height);
    _cam.copy(dirMount).applyQuaternion(_invCam);
    // NDC times the depth: the same ray whether the direction is in front or behind.
    const sx = _cam.x / (t * aspect);
    const sy = _cam.y / t;
    const rx = Math.max(0.05, 1 - (2 * MARGIN_PX) / Math.max(1, width));
    const ry = Math.max(0.05, 1 - (2 * MARGIN_PX) / Math.max(1, height));
    let onScreen = false;
    if (_cam.z < 0) {
      const d = -_cam.z;
      onScreen = Math.abs(sx / d) <= rx && Math.abs(sy / d) <= ry;
    }
    if (onScreen) {
      m.glyph.position.copy(camera.position).addScaledVector(dirMount, EYE_DISTANCE_M);
      m.arrow.visible = false;
      const d = -_cam.z;
      m.ndcX = sx / d;
      m.ndcY = sy / d;
    } else {
      // Dead astern: no side is nearer than any other, so take the bottom edge.
      const dead = Math.max(Math.abs(sx) / rx, Math.abs(sy) / ry) <= 1e-9;
      const dx = dead ? 0 : sx;
      const dy = dead ? -1 : sy;
      const u = clearScale(dx, dy, rx, ry, width, height, panels);
      const cx = dx * u;
      const cy = dy * u;
      m.ndcX = cx;
      m.ndcY = cy;
      _ray.set(cx * t * aspect, cy * t, -1).normalize().applyQuaternion(camera.quaternion);
      m.glyph.position.copy(camera.position).addScaledVector(_ray, EYE_DISTANCE_M);
      m.arrow.position.copy(m.glyph.position);
      m.arrow.visible = true;
      m.angleRad = Math.atan2(cy, cx);
      const amat = m.arrow.material as THREE.SpriteMaterial;
      amat.rotation = m.angleRad;
      m.arrow.scale.set((scale * ARROW_PX) / GLYPH_PX, (scale * ARROW_PX) / GLYPH_PX, 1);
    }
    m.glyph.scale.set(scale, scale, 1);
    m.glyph.visible = true;
    m.onScreen = onScreen;
  }

  const ndcToPx = (ndcX: number, ndcY: number, width: number, height: number) => (
    { x: ((ndcX + 1) / 2) * width, y: ((1 - ndcY) / 2) * height });

  /** The label canvas, rebuilt only when the string it carries has changed. */
  function syncLabel(text: string, now: number): void {
    tm.labelWanted = text;
    if (text === tm.labelText) return;
    if (now - tm.labelBuiltAt < LABEL_REBUILD_INTERVAL_MS) return;
    const canvas = labelCanvas(text, TARGET_COLOR);
    const mat = label.material as THREE.SpriteMaterial;
    mat.map?.dispose();
    const tex = new THREE.CanvasTexture(canvas);
    tex.anisotropy = 4;
    mat.map = tex;
    mat.needsUpdate = true;
    tm.labelText = text;
    tm.labelAspect = canvas.width / canvas.height;
    tm.labelBuiltAt = now;
    tm.labelBuilds++;
  }

  /**
   * The label rides the glyph's own row, on the side `labelSide` picks.
   *
   * Placed whether or not it is drawn, so `read()` answers for where it would
   * be and a check can read the pixels there.
   */
  function placeLabel(width: number, height: number): void {
    const t = Math.tan((camera.fov * Math.PI) / 360);
    const pxToWorld = (2 * EYE_DISTANCE_M * t) / Math.max(1, height);
    tm.labelHeightPx = LABEL_PX * (LABEL_TEX_H / LABEL_FONT_PX);
    tm.labelWidthPx = tm.labelHeightPx * tm.labelAspect;
    label.scale.set(tm.labelWidthPx * pxToWorld, tm.labelHeightPx * pxToWorld, 1);
    const offsetPx = GLYPH_PX / 2 + LABEL_GAP_PX + tm.labelWidthPx / 2;
    const side = labelSide(target, offsetPx, width);
    _right.set(1, 0, 0).applyQuaternion(camera.quaternion);
    label.position.copy(target.glyph.position)
      .addScaledVector(_right, side * offsetPx * pxToWorld);
  }

  /**
   * The picked body's own marker. Two rules take it away and only two:
   *
   * - Size. Its disc is already plainly on screen, drawn wider than the glyph
   *   that would point at it, so the glyph and the name both go. Off screen it
   *   draws however big the body is - a disc you are facing away from is
   *   exactly when the pointer is still wanted.
   * - Clash. The glyph would land on a velocity marker, so the GLYPH goes and
   *   the label stays.
   */
  function placeTarget(t: MarkerTarget | null, width: number, height: number,
    velocityDrawn: boolean, now: number): void {
    tm.picked = t !== null;
    tm.suppressed = null;
    if (t === null) {
      tm.radiusPx = 0;
      tm.rangeM = 0;
      tm.labelWanted = '';
      tm.sizeHidden = false;
      hide(target);
      label.visible = false;
      return;
    }
    // `?targetmarkoff=1` suppresses the DRAWING, not the reading: the range,
    // the radius and the label the caller handed over are still reported, so
    // the control that opens it reads as "nothing is drawn" and not as "the
    // handle went blank", which are different faults.
    tm.rangeM = t.rangeM;
    tm.radiusPx = bodyRadiusPx(t.radiusM, t.rangeM, camera.fov, height);
    if (targetOff) {
      tm.labelWanted = t.label;
      tm.sizeHidden = false;
      hide(target);
      label.visible = false;
      return;
    }
    syncLabel(t.label, now);
    _tgt.copy(t.dir).normalize().applyQuaternion(config.toMount());
    place(target, _tgt, width, height);
    placeLabel(width, height);
    if (!targetAlways) {
      // The clear frame is the same rect `place()` calls a direction on screen
      // in, so the two readings cannot disagree about where the frame ends.
      if (!target.onScreen) tm.sizeHidden = false;
      else if (tm.sizeHidden) tm.sizeHidden = tm.radiusPx > GLYPH_PX * TARGET_SHOW_FRACTION;
      else tm.sizeHidden = tm.radiusPx > GLYPH_PX;
      if (tm.sizeHidden) {
        tm.suppressed = 'size';
      } else if (velocityDrawn) {
        const px = ndcToPx(target.ndcX, target.ndcY, width, height);
        for (const m of [prograde, retrograde]) {
          if (!m.glyph.visible) continue;
          const q = ndcToPx(m.ndcX, m.ndcY, width, height);
          if (Math.hypot(px.x - q.x, px.y - q.y) <= TARGET_CLASH_PX) {
            tm.suppressed = 'clash';
            break;
          }
        }
      }
    }
    if (tm.suppressed !== null) {
      target.glyph.visible = false;
      target.arrow.visible = false;
    }
    label.visible = tm.suppressed !== 'size';
  }

  /**
   * Where the flight assist is bringing the motion round to, on the same ring
   * prograde and retrograde ride: the gap it is about to spend delta-v closing
   * is visible before the burn rather than after it. Hidden with no aim (the
   * assist is off, or the caller passed null), and hidden within
   * ASSIST_AIM_HIDE_DEG of prograde, where it would sit on top of that glyph.
   */
  function placeAssistAim(aim: THREE.Vector3 | null, width: number, height: number,
    velocityDrawn: boolean): void {
    if (aim === null) {
      hide(aimMarker);
      aimLabel.visible = false;
      return;
    }
    _aim.copy(aim).applyQuaternion(config.toMount());
    if (velocityDrawn && (_aim.angleTo(_dir) * 180) / Math.PI < ASSIST_AIM_HIDE_DEG) {
      hide(aimMarker);
      aimLabel.visible = false;
      return;
    }
    place(aimMarker, _aim, width, height);
    const t = Math.tan((camera.fov * Math.PI) / 360);
    const pxToWorld = (2 * EYE_DISTANCE_M * t) / Math.max(1, height);
    aimLabel.scale.set(aimLabelWidthPx * pxToWorld, aimLabelHeightPx * pxToWorld, 1);
    const offsetPx = GLYPH_PX / 2 + LABEL_GAP_PX + aimLabelWidthPx / 2;
    const side = labelSide(aimMarker, offsetPx, width);
    _right.set(1, 0, 0).applyQuaternion(camera.quaternion);
    aimLabel.position.copy(aimMarker.glyph.position)
      .addScaledVector(_right, side * offsetPx * pxToWorld);
    aimLabel.visible = true;
  }

  /**
   * Where the delta-v map's armed plan wants the nose: the plan's own departure
   * asymptote until the trip leaves, and the COURSE FIX row's own direction
   * after it. Drawn on the same ring the velocity pair rides, so a pilot flying
   * the map by hand can point at it the way they point at prograde. Hidden with
   * no plan armed and while GO has the stick, which is when the autopilot is
   * doing the pointing.
   */
  function placeBurn(burn: MarkerBurn | null, width: number, height: number): void {
    if (burn === null) {
      hide(burnMarker);
      burnLabel.visible = false;
      bm.kind = null;
      bm.dir = null;
      return;
    }
    bm.kind = burn.kind;
    bm.dir = { x: burn.dir.x, y: burn.dir.y, z: burn.dir.z };
    _burn.copy(burn.dir).applyQuaternion(config.toMount());
    place(burnMarker, _burn, width, height);
    if (burn.label !== bm.labelText) {
      const canvas = labelCanvas(burn.label, BURN_COLOR);
      const mat = burnLabel.material as THREE.SpriteMaterial;
      mat.map?.dispose();
      const tex = new THREE.CanvasTexture(canvas);
      tex.anisotropy = 4;
      mat.map = tex;
      mat.needsUpdate = true;
      bm.labelText = burn.label;
      bm.labelAspect = canvas.width / canvas.height;
    }
    const t = Math.tan((camera.fov * Math.PI) / 360);
    const pxToWorld = (2 * EYE_DISTANCE_M * t) / Math.max(1, height);
    bm.labelHeightPx = LABEL_PX * (LABEL_TEX_H / LABEL_FONT_PX);
    bm.labelWidthPx = bm.labelHeightPx * bm.labelAspect;
    burnLabel.scale.set(bm.labelWidthPx * pxToWorld, bm.labelHeightPx * pxToWorld, 1);
    const offsetPx = GLYPH_PX / 2 + LABEL_GAP_PX + bm.labelWidthPx / 2;
    const side = labelSide(burnMarker, offsetPx, width);
    _right.set(1, 0, 0).applyQuaternion(camera.quaternion);
    burnLabel.position.copy(burnMarker.glyph.position)
      .addScaledVector(_right, side * offsetPx * pxToWorld);
    burnLabel.visible = true;
  }

  function update(velocityFlight: THREE.Vector3, t: MarkerTarget | null,
    assistAim: THREE.Vector3 | null, burn: MarkerBurn | null = null): void {
    speedMps = velocityFlight.length();
    const width = window.innerWidth;
    const height = window.innerHeight;
    const velocityDrawn = !suppressed && speedMps > MIN_SPEED_MPS;
    const targetDrawn = !targetOff && t !== null;
    // A handle drawing nothing asks the DOM for nothing: the rect read is a
    // layout flush, and there is no clamp to solve with no marker on it.
    if (velocityDrawn || targetDrawn || burn !== null) refreshPanels(performance.now());
    _invCam.copy(camera.quaternion).invert();
    if (velocityDrawn) {
      _dir.copy(velocityFlight).divideScalar(speedMps).applyQuaternion(config.toMount());
      _opp.copy(_dir).negate();
      place(prograde, _dir, width, height);
      place(retrograde, _opp, width, height);
    } else {
      hide(prograde);
      hide(retrograde);
    }
    placeAssistAim(assistAim, width, height, velocityDrawn);
    placeBurn(burn, width, height);
    placeTarget(t, width, height, velocityDrawn, performance.now());
  }

  /** One sprite's own drawn place, projected by the same camera that drew it. */
  function projectPx(s: THREE.Sprite, width: number, height: number): {
    ndc: { x: number; y: number }; px: { x: number; y: number };
  } {
    s.getWorldPosition(_proj).applyMatrix4(_vp);
    return {
      ndc: { x: _proj.x, y: _proj.y },
      px: { x: ((_proj.x + 1) / 2) * width, y: ((1 - _proj.y) / 2) * height },
    };
  }

  function readOne(m: Marker, width: number, height: number): ProgradeMarkerRead {
    return {
      visible: m.glyph.visible,
      onScreen: m.onScreen,
      ...projectPx(m.glyph, width, height),
      arrow: { visible: m.arrow.visible, angleRad: m.angleRad },
    };
  }

  function read(): ProgradeMarkersRead {
    const width = window.innerWidth;
    const height = window.innerHeight;
    camera.updateWorldMatrix(true, false);
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
    _vp.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    camera.getWorldPosition(_eye);
    group.getWorldQuaternion(_quat).multiply(config.toMount());
    return {
      speedMps,
      suppressed,
      eyeDistanceM: EYE_DISTANCE_M,
      glyphPx: GLYPH_PX,
      fovDeg: camera.fov,
      marginPx: MARGIN_PX,
      panelClearPx: PANEL_CLEAR_PX,
      hudInsets,
      panels: panels.map((p) => ({ ...p })),
      viewport: { width, height },
      eyeWorld: { x: _eye.x, y: _eye.y, z: _eye.z },
      viewProjection: Array.from(_vp.elements),
      flightToWorld: { x: _quat.x, y: _quat.y, z: _quat.z, w: _quat.w },
      prograde: readOne(prograde, width, height),
      retrograde: readOne(retrograde, width, height),
      assistAim: {
        ...readOne(aimMarker, width, height),
        labelMark: {
          visible: aimLabel.visible,
          px: projectPx(aimLabel, width, height).px,
          widthPx: aimLabelWidthPx,
          heightPx: aimLabelHeightPx,
        },
      },
      burnMark: {
        ...readOne(burnMarker, width, height),
        kind: bm.kind,
        label: bm.labelText,
        dir: bm.dir === null ? null : { ...bm.dir },
        labelMark: {
          visible: burnLabel.visible,
          px: projectPx(burnLabel, width, height).px,
          widthPx: bm.labelWidthPx,
          heightPx: bm.labelHeightPx,
        },
      },
      targetMark: {
        picked: tm.picked,
        off: targetOff,
        always: targetAlways,
        label: tm.labelText,
        labelWanted: tm.labelWanted,
        labelBuilds: tm.labelBuilds,
        labelHold,
        ...readOne(target, width, height),
        bodyRadiusPx: tm.radiusPx,
        rangeM: tm.rangeM,
        suppressed: tm.suppressed,
        labelMark: {
          visible: label.visible,
          px: projectPx(label, width, height).px,
          widthPx: tm.labelWidthPx,
          heightPx: tm.labelHeightPx,
        },
      },
    };
  }

  function dispose(): void {
    window.removeEventListener('resize', invalidatePanels);
    const sprites = [label, aimLabel, burnLabel];
    for (const m of [prograde, retrograde, target, aimMarker, burnMarker]) {
      sprites.push(m.glyph, m.arrow);
    }
    for (const s of sprites) {
      const mat = s.material as THREE.SpriteMaterial;
      mat.map?.dispose();
      mat.dispose();
      s.removeFromParent();
    }
    group.removeFromParent();
  }

  return { group, update, read, dispose };
}
